import { type MetaDescriptor } from '@remix-run/react'
import { PLAN_DETAILS } from './plan-catalog.ts'

/** Public support mailbox. One address for the site and contact email. */
export const SUPPORT_EMAIL = 'support@reefchronicles.com'

const freeTanks = PLAN_DETAILS.free.tanks

export const HOME_TITLE =
	'ReefChronicles: Reef Tank Logbook & Dosing Calculator'

export const HOME_DESCRIPTION = `Log reef tank tests, see trends against target ranges, dose calcium, alkalinity and magnesium, and keep maintenance logs and galleries. Free for up to ${freeTanks} tanks.`

export const PRICING_DESCRIPTION = `ReefChronicles plans. Free includes ${freeTanks} tanks. Pro and Premium raise tank, parameter log, coral analysis, and gallery limits. The dosing calculator stays free.`

const DEFAULT_ORIGIN = 'https://tankmate.tech'

/**
 * Origin from the root loader's public env. Meta runs on the server and in
 * the browser, so this reads serialized loader data instead of process.env.
 */
export function getAppOrigin(
	matches: ReadonlyArray<{ id: string; data?: unknown }>,
) {
	const root = matches.find((match) => match.id === 'root')
	const data = root?.data
	if (data && typeof data === 'object' && 'ENV' in data) {
		const origin = (data as { ENV?: { APP_ORIGIN?: unknown } }).ENV?.APP_ORIGIN
		if (typeof origin === 'string' && origin.length > 0) return origin
	}
	return DEFAULT_ORIGIN
}

export function marketingMeta({
	title,
	description,
	url,
	image,
	imageAlt,
	ogTitle = title,
	ogDescription = description,
	twitterTitle = ogTitle,
	twitterDescription = ogDescription,
}: {
	title: string
	description: string
	url: string
	image: string
	imageAlt: string
	ogTitle?: string
	ogDescription?: string
	twitterTitle?: string
	twitterDescription?: string
}): MetaDescriptor[] {
	return [
		{ title },
		{ name: 'description', content: description },
		{ tagName: 'link', rel: 'canonical', href: url },
		{ name: 'theme-color', content: '#020617' },
		{ name: 'apple-mobile-web-app-title', content: 'ReefChronicles' },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:site_name', content: 'ReefChronicles' },
		{ property: 'og:title', content: ogTitle },
		{ property: 'og:description', content: ogDescription },
		{ property: 'og:url', content: url },
		{ property: 'og:image', content: image },
		{ property: 'og:image:alt', content: imageAlt },
		{ property: 'og:image:width', content: '1200' },
		{ property: 'og:image:height', content: '630' },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:title', content: twitterTitle },
		{ name: 'twitter:description', content: twitterDescription },
		{ name: 'twitter:image', content: image },
		{ name: 'twitter:image:alt', content: imageAlt },
	]
}

export const OG_IMAGE_PATH = '/og-image.png'

export const OG_IMAGE_ALT =
	'ReefChronicles homepage: your whole reef, in one logbook'
